/**
 * @name getSortQueryParams
 * @desc Provides the sort query parameter for a search url, based on the table columns being sorted
 * @param {object} sortBy The current table columns being sorted, for example {name: 'asc', age: 'desc'}
 * @param {object} columnSortModifiers the configuration object used to customize sorting for each column in the table
 */
export const getSortQueryParams = (sortBy, columnSortModifiers) => {
  if (!sortBy) {
    return '';
  }
  const sortIds = [];
  const defaultSortIds = [];
  Object.keys(sortBy).forEach(colId => {
    const modifiedColId = columnSortModifiers[colId]?.colId;
    const sortId = sortBy[colId] === 'asc' ? `${modifiedColId || colId}Asc` : `${modifiedColId || colId}Desc`;
    if (columnSortModifiers?.defaultSort?.[colId]) {
      defaultSortIds.push(sortId);
    } else {
      sortIds.push(sortId);
    }
  });
  if (sortIds.length <= 0 && defaultSortIds.length <= 0) {
    return '';
  }
  // requirement to ensure that default sorts come after the other sorts
  const columnParams = [...sortIds, ...defaultSortIds];
  return `&sort=${columnParams.join(',')}`;
};

/**
 * @name getFilterQueryParams
 * @desc Provides the sort query parameter for a search url, based on the table columns being sorted
 * @param {array} filters The current table columns being filtered, for example [{id: 'name', value: 'John'}]
 * @param {object} columnFilterModifiers the configuration object used to customize filtering for each column
 */
export const getFilterQueryParams = (filters, columnFilterModifiers) => {
  if (!filters || filters.length <= 0) {
    return '';
  }

  const filterParams = filters.map(filter => {
    const columnFilterModifier = columnFilterModifiers[filter.id];

    const modifiedColId =
      typeof columnFilterModifier?.colId === 'function'
        ? columnFilterModifier.colId(filter.value)
        : columnFilterModifier?.colId;

    const modifiedValue = columnFilterModifier?.value ? columnFilterModifier.value(filter.value) : filter.value;

    const expression = columnFilterModifier?.expression || '';
    const colId = modifiedColId || filter?.id;
    const filterValue = modifiedValue || filter?.value;

    return `filter=${colId}${expression}(${filterValue})`;
  });

  return `&${filterParams.join('&')}`;
};

/**
 * @name scrollbarWidth
 * @desc Provides the width of the browser scrollbar. See https://davidwalsh.name/detect-scrollbar-width
 * @return {number} The width of the scrollbar in pixels
 */
export const scrollbarWidth = () => {
  const scrollDiv = document.createElement('div');
  scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;');
  document.body.appendChild(scrollDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};
